import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  IconButton,
  IconClose,
  PageHeaderNavContext,
  useEscape,
  useFocusTrap,
} from '../../../ui/v2';
import { HotelSidebar } from './HotelSidebar';
import {
  isLanding,
  rememberExperience,
  rememberHotelPath,
  useExperiences,
} from './experiences';

/**
 * Layout route for `/hotel/*` (components doc §2, IA §2). A sibling of
 * `AppLayout`, never its child: hotel pages must not render inside
 * restaurant chrome, whatever the frame flag says.
 *
 * Owns the pinned sidebar, the desktop collapse preference, the drawer
 * below 1024px, and the `PageHeaderNavContext` that gives every hotel
 * `Workspace` its menu button. Imports nothing private from
 * `src/layout/AppFrameV2.tsx`; the shapes match on purpose, the code does
 * not have to.
 *
 * Mount it as `<Route element={<HotelShell />}>` around the `/hotel/*`
 * route; `HotelApp` renders through `<Outlet />`.
 */

/** Shared with the restaurant frame on purpose (IA §2 "may reuse"). */
export const NAV_COLLAPSED_KEY = 'av2.nav.collapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

/** Tailwind `lg`: the pinned sidebar shows, the drawer does not. */
const DESKTOP = '(min-width: 1024px)';

const isDesktop = () =>
  typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP).matches;

const HotelShell: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { pathname, key: locationKey } = useLocation();
  const { user } = useAuth();
  const userId = user?.id;
  const { hasHotel } = useExperiences();
  // Someone with no hotel (every restaurant) gets the bare Not found that
  // `HotelApp` renders, as for any unknown URL: no hotel chrome around it.
  // Until that is known, `HotelApp`'s loading body stands alone too, as the
  // restaurant shell waits without its chrome.
  const bare = !hasHotel;

  // The URL is the source of truth; this is only remembered for landing
  // pages and for the switcher's way back (IA §3). Arriving here first uses
  // up the page load's landing, so a later `/` stays the restaurant.
  useEffect(() => {
    if (userId === undefined || !hasHotel) return;
    rememberExperience(userId, 'hotel');
    isLanding(userId, locationKey);
  }, [userId, hasHotel, locationKey]);
  useEffect(() => {
    if (userId !== undefined && hasHotel) rememberHotelPath(userId, pathname);
  }, [userId, hasHotel, pathname]);

  useEffect(() => setDrawerOpen(false), [pathname]);
  // The drawer is `lg:hidden`: widening past 1024px hides it, and its focus
  // trap would keep Tab cycling through controls nobody can see.
  useEffect(() => {
    if (!drawerOpen || typeof window.matchMedia !== 'function') return;
    const desktop = window.matchMedia(DESKTOP);
    const close = () => {
      if (desktop.matches) setDrawerOpen(false);
    };
    desktop.addEventListener('change', close);
    return () => desktop.removeEventListener('change', close);
  }, [drawerOpen]);
  // The restaurant drawer does not close on Escape; this one does. Focus
  // starts on Close, stays in the drawer, and returns to the opener (the
  // `Workspace` menu button) when it closes.
  const drawerRef = useRef<HTMLDivElement>(null);
  useEscape(drawerOpen, () => setDrawerOpen(false));
  useFocusTrap(drawerOpen, drawerRef);

  const setCollapsedPreference = (next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* preference lasts for this page load */
    }
  };

  // Desktop re-pins the hidden sidebar; smaller screens open the drawer.
  const openNavigation = () => {
    if (collapsed && isDesktop()) setCollapsedPreference(false);
    else setDrawerOpen(true);
  };
  const openNavRef = useRef(openNavigation);
  openNavRef.current = openNavigation;

  const navContext = useMemo(
    () => ({
      open: () => openNavRef.current(),
      sidebarHidden: collapsed,
      // Every hotel page is a Workspace; the frame never adds its own bar.
      frameProvidesNav: false,
    }),
    [collapsed],
  );

  if (bare) return <Outlet />;

  return (
    <PageHeaderNavContext.Provider value={navContext}>
      <div className="av2 flex h-screen w-full overflow-hidden">
        {!collapsed && (
          <div className="hidden h-full lg:block">
            <HotelSidebar onCollapse={() => setCollapsedPreference(true)} />
          </div>
        )}
        {drawerOpen && (
          <div className="fixed inset-0 z-[60] flex lg:hidden">
            <div
              aria-hidden
              className="absolute inset-0 bg-v2-fg/30"
              onClick={() => setDrawerOpen(false)}
            />
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
              tabIndex={-1}
              className="v2-slide-in-right relative h-full shadow-v2-panel outline-none"
            >
              <HotelSidebar onNavigate={() => setDrawerOpen(false)} />
              <IconButton
                label="Close navigation"
                data-autofocus="true"
                onClick={() => setDrawerOpen(false)}
                className="v2-inverse absolute right-3 top-4"
              >
                <IconClose />
              </IconButton>
            </div>
          </div>
        )}
        {/* Focusable for the experience switcher's hand-off below 1024px. */}
        <main
          tabIndex={-1}
          className="relative flex min-w-0 flex-1 flex-col overflow-hidden bg-v2-canvas outline-none"
        >
          {/* HotelApp: gate → GroupPicker | NotFound | PropertyScope → FillPage → Workspace pages */}
          <Outlet />
        </main>
      </div>
    </PageHeaderNavContext.Provider>
  );
};

export default HotelShell;
